import { inject, injectable } from 'tsyringe';
import { BaseViewModel } from '@/shared/viewmodels/BaseViewModel';
import { NotificationsApiService } from './NotificationsApiService';
import type {
    EffectiveNotificationPreference,
    NotificationChannel,
    PreferenceIssue,
} from './NotificationTypes';

/** Identifies one saveable control — a channel toggle or an option input. */
function savingKey(templateType: string, control: string): string {
    return `${templateType}:${control}`;
}

export interface SavePreferenceOutcome {
    ok: boolean;
    /** The policy's objection when the change was refused. */
    issue?: PreferenceIssue;
}

/**
 * State behind the Settings → Notifications preferences tab.
 *
 * Each toggle saves immediately rather than batching behind a Save button: the
 * service validates one notification type at a time and can refuse a change
 * (security alerts cannot be fully silenced), so per-toggle saves let the UI
 * revert exactly the switch that was rejected and explain why.
 */
@injectable()
export class NotificationPreferencesViewModel extends BaseViewModel {
    private preferenceList: EffectiveNotificationPreference[] = [];
    private isLoading = true;
    private loadFailed = false;
    private savingKeys = new Set<string>();

    constructor(@inject(NotificationsApiService) private readonly api: NotificationsApiService) {
        super();
    }

    get preferences(): EffectiveNotificationPreference[] {
        return this.preferenceList;
    }

    get loading(): boolean {
        return this.isLoading;
    }

    get failed(): boolean {
        return this.loadFailed;
    }

    isSaving(templateType: string, control: string): boolean {
        return this.savingKeys.has(savingKey(templateType, control));
    }

    async load(): Promise<void> {
        this.isLoading = true;
        this.notifyChange();
        try {
            this.preferenceList = await this.api.getPreferences();
            this.loadFailed = false;
        } catch {
            this.loadFailed = true;
        }
        this.isLoading = false;
        this.notifyChange();
    }

    /**
     * Flip one channel and persist it. The switch moves optimistically and snaps
     * back if the policy (or the network) refuses, so the rendered state always
     * matches what is actually stored.
     */
    async toggleChannel(
        templateType: string,
        channel: NotificationChannel,
    ): Promise<SavePreferenceOutcome> {
        const preference = this.preferenceList.find((p) => p.templateType === templateType);
        if (!preference) return { ok: false };

        const next = !preference.channels[channel].enabled;
        const key = savingKey(templateType, channel);

        this.applyChannel(templateType, channel, next);
        this.savingKeys.add(key);
        this.notifyChange();

        try {
            const result = await this.api.updatePreference(templateType, { [channel]: next });
            if (!result.valid) {
                this.applyChannel(templateType, channel, !next);
                return { ok: false, issue: result.issues?.[0] };
            }
            return { ok: true };
        } catch {
            this.applyChannel(templateType, channel, !next);
            return { ok: false };
        } finally {
            this.savingKeys.delete(key);
            this.notifyChange();
        }
    }

    /**
     * Set one option value and persist it. Same optimistic-then-revert contract
     * as {@link toggleChannel} — the server validates the value against the
     * notification type's authored choices, so a rejected value snaps back.
     */
    async setOption(
        templateType: string,
        optionId: string,
        value: string,
    ): Promise<SavePreferenceOutcome> {
        const preference = this.preferenceList.find((p) => p.templateType === templateType);
        const option = preference?.options.find((o) => o.id === optionId);
        if (!option || option.value === value) return { ok: true };

        const previous = option.value;
        const key = savingKey(templateType, optionId);

        this.applyOption(templateType, optionId, value);
        this.savingKeys.add(key);
        this.notifyChange();

        try {
            const result = await this.api.updatePreference(templateType, { options: { [optionId]: value } });
            if (!result.valid) {
                this.applyOption(templateType, optionId, previous);
                return { ok: false, issue: result.issues?.[0] };
            }
            return { ok: true };
        } catch {
            this.applyOption(templateType, optionId, previous);
            return { ok: false };
        } finally {
            this.savingKeys.delete(key);
            this.notifyChange();
        }
    }

    private applyChannel(templateType: string, channel: NotificationChannel, enabled: boolean): void {
        this.preferenceList = this.preferenceList.map((p) =>
            p.templateType === templateType
                ? {
                      ...p,
                      channels: {
                          ...p.channels,
                          [channel]: { ...p.channels[channel], enabled, stored: enabled },
                      },
                  }
                : p,
        );
    }

    private applyOption(templateType: string, optionId: string, value: string): void {
        this.preferenceList = this.preferenceList.map((p) =>
            p.templateType === templateType
                ? {
                      ...p,
                      options: p.options.map((o) =>
                          o.id === optionId ? { ...o, value, customized: value !== o.defaultValue } : o,
                      ),
                  }
                : p,
        );
    }
}
